import React from "react";
import { useTranslation } from "react-i18next";
import {
  CalendarCheck,
  CalendarClock,
  CalendarX,
  TrendingUp,
} from "lucide-react";
import type { DateRange } from "@/hooks/useDateRange";
import { formatMinorCurrency } from "@/utils/billingBalance";
import {
  StatsSummaryBlock,
  type StatsSummaryCard,
} from "@/components/common/StatsSummaryBlock";
import { useReservationSummaryStats } from "../hooks/useReservationSummaryStats";

interface ReservationStatsProps {
  dateRange: DateRange;
}

const formatValue = (
  isLoading: boolean,
  value: React.ReactNode | null | undefined,
): React.ReactNode => {
  if (isLoading) {
    return "…";
  }
  return value ?? "—";
};

const ReservationStats: React.FC<ReservationStatsProps> = ({ dateRange }) => {
  const { t } = useTranslation();
  const stats = useReservationSummaryStats(dateRange);

  const upsellValue = stats.upSell
    ? formatMinorCurrency(stats.upSell.net, stats.upSell.currency)
    : null;

  const cards: StatsSummaryCard[] = [
    {
      key: "upsell",
      label: t("reservation.stats.upsell", "Upsell"),
      value: formatValue(stats.isLoading, upsellValue),
      icon: TrendingUp,
      valueClassName: "text-green-600",
    },
    {
      key: "activeReservations",
      label: t("reservation.stats.activeReservations", "Active Reservations"),
      value: formatValue(stats.isLoading, stats.activeReservations),
      icon: CalendarCheck,
      valueClassName: "text-green-600",
    },
    {
      key: "canceledReservations",
      label: t(
        "reservation.stats.canceledReservations",
        "Canceled Reservations",
      ),
      value: formatValue(stats.isLoading, stats.canceledReservations),
      icon: CalendarX,
      valueClassName: "text-red-600",
    },
    {
      key: "avgLengthOfStay",
      label: t("reservation.stats.avgLengthOfStay", "Avg. Length of Stay"),
      value: formatValue(stats.isLoading, stats.avgLengthOfStayNights),
      icon: CalendarClock,
      valueClassName: "text-eathy-comfort-1",
    },
  ];

  return <StatsSummaryBlock cards={cards} className="my-6" />;
};

export default ReservationStats;
